import { useEffect, useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DecimalInput } from '@/components/common/DecimalInput';
import { InvoicePickerCombobox } from '@/components/accounts-receivable/collection-threads/InvoicePickerCombobox';
import { useCreateArPaymentApplication } from '@/api/rpc/hooks/accounts-receivable/useArPaymentApplications';
import { useToast } from 'src/context/ToastContext';

interface ApplyToInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentId: string;
  /** Restrict the invoice picker to this customer when known. */
  customerId?: string | null;
  /** Pre-fill the applied amount (e.g. the payment's unapplied remainder). */
  defaultAmount?: string | null;
}

export function ApplyToInvoiceDialog({
  open,
  onOpenChange,
  paymentId,
  customerId,
  defaultAmount,
}: ApplyToInvoiceDialogProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const createMutation = useCreateArPaymentApplication();

  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [amount, setAmount] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setInvoiceId(null);
    setAmount(defaultAmount ?? null);
  }, [open, defaultAmount]);

  // Require a parseable, strictly-positive amount so partial tokens like "-"
  // or "." can't be submitted (which the server would reject anyway).
  const amountValue = amount === null ? NaN : Number(amount);
  const canSubmit =
    invoiceId !== null &&
    Number.isFinite(amountValue) &&
    amountValue > 0 &&
    !createMutation.isPending;

  async function handleSubmit() {
    if (!canSubmit || invoiceId === null || amount === null) return;
    try {
      await createMutation.mutateAsync({
        paymentId,
        invoiceId,
        appliedAmount: amount,
      });
      showToast({
        title: gt('Allocation added', {
          context: 'toast title, payment applied to invoice',
        }),
        color: 'success',
        icon: 'check',
      });
      onOpenChange(false);
    } catch (err) {
      showToast({
        title: gt('Allocation failed', {
          context: 'toast title, payment application failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title, apply payment to an invoice">
              Apply to invoice
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="dialog description, allocate part of a payment to an invoice">
              Allocate part of this payment to an open invoice.
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>
              <T context="form label, invoice to apply payment to">Invoice</T>
            </Label>
            <InvoicePickerCombobox
              customerId={customerId ?? undefined}
              value={invoiceId}
              onChange={setInvoiceId}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="apply-amount">
              <T context="form label, amount applied to invoice">Amount</T>
            </Label>
            <DecimalInput
              id="apply-amount"
              value={amount}
              onChange={setAmount}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            <T context="button label, cancel dialog">Cancel</T>
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            <T context="button label, apply payment to invoice">Apply</T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
